import { Box, ClickAwayListener } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import useEventListener from '../../hooks/useEventListener.hooks';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import Colors from '../../utils/color';
import IssueBacklogList, { IssueBacklogListHandle } from './IssueBacklogList';

/**
 * How often the ages on the rows are re-stamped.
 *
 * The popover stamps once on open, which is right for a surface that lives for
 * seconds. This one can be on screen for the whole gap between cars, so a single
 * stamp would leave a row reading `(2m)` twenty minutes later. 30s is free — one
 * `setState` — and the display rounds to minutes anyway, so it is never more than
 * half a minute stale in a field that cannot show half minutes.
 */
export const LOBBY_AGE_TICK_MS = 30000;

interface IssueBacklogLobbyCardProps {
  /**
   * Task-router auto-assignment mode. Hidden in legacy manual mode, and that is a
   * SAFETY gate, not a product preference — see below.
   */
  isTaskAssignmentMode: boolean;
}

/**
 * HITL-1575: the outstanding backlog on the task-router lobby, so the leftover work is
 * reachable in the idle time between cars.
 *
 * The second host for `IssueBacklogList`, the same component the Issue button's
 * popover mounts. This is the moment the whole deferred-classification feature exists
 * for: the agent has no car, no order and nothing else to do, which is exactly when
 * classifying a report is cheap. Everything about the panel — the 10s window, the
 * pausing clock, `Later` — is a compromise with a live drive-thru. Here there is none.
 *
 * ## Open when non-empty, never collapsed
 *
 * No toggle and no "3 issues" pill to click first. An extra click to reveal the work
 * is an extra chance to skip it, and this is the surface with no competing demand on
 * the agent's attention. Renders nothing at all when the backlog is empty, so a clean
 * lobby stays clean.
 *
 * ## All three agent statuses
 *
 * `available`, `unavailable` and `training`. Deliberately not gated on status:
 * `unavailable` is where this work realistically gets done, and gating it there would
 * remove the surface from the only state whose entire purpose is not taking cars.
 *
 * ## Hidden in legacy manual mode — a safety gate
 *
 * `TaskRouterAcceptance` registers a **window-level `keyup`** and accepts the offered
 * task on ANY keyboard event (`TaskRouterAcceptance/index.tsx:91-97`). A text field on
 * screen at the same time would accept a task on the agent's first keystroke.
 *
 * That component renders only via `TaskRouterAgentAvailable`, which is the
 * `!isTaskAssignmentMode` branch — so hiding this card in legacy mode means the two can
 * never be mounted together, which removes the hazard rather than working around it.
 * Mounting as a SIBLING of `TaskRouterBody` rather than inside it is the second layer:
 * even if that ever changed, this is not in the acceptance subtree.
 *
 * ## The auto-assignment yank needs no handling
 *
 * Auto-assignment has no accept step — `assignedTaskId` navigates the instant a task
 * arrives — so this card can be pulled off the screen mid-sentence with no warning and
 * nothing to hold. It needs no guard because `IssueClassifyBody` commits a typed detail
 * on unmount: the text is recorded, the report stays outstanding because it still has
 * no type, and it is behind the Issue badge by the time the agent lands on the console.
 * An interrupted classification self-heals. No hold on navigation, no prompt, no
 * change to `useSessionStatus`.
 *
 * ## Position — in the flow, at the bottom of the page's column
 *
 * NOT floating. This shipped as `position: fixed` bottom-left to avoid touching the
 * page's layout, and that was the wrong trade: the card covered the centred "Get
 * ready / New tasks will be offered to you shortly" message, which is the one thing
 * this screen exists to say. An overlay that hides the page's own content is not a
 * cheaper option, it is a broken one.
 *
 * `TaskRouterPage`'s root is now a flex column (`height: 100%`), so this is a normal
 * item at the end of it and `TaskRouterBody` — whose definite height becomes its flex
 * BASIS — shrinks by exactly this card's height. The message re-centres in the space
 * that is left, nothing overlaps, and the page still never scrolls. `TaskRouterBody`
 * itself is untouched, snapshot included.
 *
 * `marginTop: auto` is what keeps it at the BOTTOM in the WS-error branch, where the
 * alert is `position: absolute` and this card is the only item in the column. In the
 * normal branch the body has already claimed the free space, so the auto margin
 * resolves to zero and changes nothing.
 *
 * `maxHeight: 50vh` is not decoration: the body can only shrink to its own content, so
 * an uncapped card — 50 retained reports, or one expanded row with the full 24-chip
 * taxonomy — would be the one thing that could force a scrollbar onto this screen.
 * Rows scroll inside the card instead, and an expanded row is never clipped because
 * the container scrolls as a whole.
 */
const IssueBacklogLobbyCard = ({
  isTaskAssignmentMode,
}: IssueBacklogLobbyCardProps) => {
  // `isEnabled` is selectIsIssueReportingEnabled — hitl_3_0 AND
  // hitl_issue_reporting, ANDed in one place. Do not re-AND it here.
  const { isEnabled, outstandingReports } = useIssueReport();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const listRef = useRef<IssueBacklogListHandle>(null);

  /**
   * Click away and Escape COLLAPSE the open row — they do not hide the card.
   *
   * The popover's versions of these gestures dismiss the whole surface, because there
   * the list is a thing that opened over the console and closing it returns the agent
   * to their order. Here the list IS the page's content: there is nothing behind it to
   * get back to, and hiding the only work on an idle lobby would be a dead end. So
   * both gestures step back one level, from the 24-chip taxonomy to the list of
   * reports, which is the state the agent came from.
   *
   * The typed detail survives either way — `IssueClassifyBody` commits on unmount, so
   * a collapse records exactly what switching rows records.
   */
  const handleEscape = (event: Event) => {
    const e = event as KeyboardEvent;
    if (e.key !== 'Escape') return;
    // Stands down for a dialog, the same check the panel and the popover make: this
    // captures on `window`, so without it an Escape meant for the take-over prompt
    // would be swallowed here.
    if (
      typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]')
    ) {
      return;
    }
    // Consume the press ONLY if a row was actually open. With the list idle, Escape
    // belongs to whatever else is on the page.
    if (!listRef.current?.collapse()) return;
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * WINDOW CAPTURE, for the same reason the panel does it: focus is inside the open
   * row's detail field or on nothing at all, and a React handler on this subtree would
   * miss the press in the second case entirely.
   */
  useEventListener('keydown', handleEscape, window, true);

  const isVisible =
    isEnabled && isTaskAssignmentMode && outstandingReports.length > 0;

  // Gated on visibility rather than mounted unconditionally, so a lobby with no
  // outstanding work runs no timer at all.
  useEffect(() => {
    if (!isVisible) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), LOBBY_AGE_TICK_MS);
    return () => clearInterval(timer);
  }, [isVisible]);

  // AFTER every hook — an early return above one changes the hook count between
  // renders, which React rejects outright.
  if (!isVisible) return null;

  return (
    // Collapses the open row rather than dismissing the card — see `handleEscape`.
    // `onClickAway` fires for a press anywhere outside the card, the lobby's own empty
    // space included, which on this page is the whole gesture: there is nothing else
    // here to click.
    <ClickAwayListener onClickAway={() => listRef.current?.collapse()}>
      <Box
        // The shared issue-reporting opt-out marker. Inert on this route — the
        // agent-interception guards live in MainPageLayout and are console-only — but
        // every classification surface carries it, and the cost of it being missing if
        // this ever renders somewhere guarded is a take-over prompt on every keystroke.
        //
        // Deliberately NOT `data-caret-owner`: its only consumer is `focusOmniBar`, and
        // there is no omnibar on the lobby to hold the caret against or hand it back to.
        // Claiming ownership of a caret nothing contests would just be a lie in the DOM.
        data-issue-report="true"
        data-testid="issue-backlog-lobby-card"
        role="region"
        aria-label="Issues to classify"
        sx={{
          // Bottom of the page's column, left-aligned, and never stretched to the
          // column's full width — see the note on `marginTop: auto` above.
          alignSelf: 'flex-start',
          flexShrink: 0,
          mt: 'auto',
          ml: 2,
          mb: 2,
          // The same 860 the flag panel and the popover use: the width at which the
          // widest chip row fits without wrapping, which is what lets a row expand into
          // the full taxonomy without a scrollbar hiding part of it.
          width: 'min(860px, calc(100vw - 32px))',
          // Bounds the number of ROWS, never the chip grid — an expanded row renders at
          // full height inside a container that scrolls as a whole. The cap is what
          // stops the card starving the body it shares the column with.
          maxHeight: '50vh',
          overflowY: 'auto',
          backgroundColor: Colors.daintree,
          color: Colors.white,
          // Same red as the Issue button's badge and the popover it opens: this is
          // outstanding work, not a confirmation. The flag panel keeps green, which
          // belongs to its tick.
          borderLeft: `4px solid ${Colors.amaranth}`,
          p: 1.5,
          boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        }}
      >
        <IssueBacklogList
          ref={listRef}
          reports={outstandingReports}
          surface="task_router_lobby"
          nowMs={nowMs}
        />
      </Box>
    </ClickAwayListener>
  );
};

export default IssueBacklogLobbyCard;
